import { createRef, Component } from "react";
import { array, bool, func, shape, string } from "prop-types";

import { URL_API_LISTINGS_CONTACT } from "../../constants";
import {
  api,
  getApiErrorMessage,
  scrollIfNeeded,
  unformatSourceDivision,
  validateInput,
} from "../../helpers";
import getValidAttribution from "../analytics/helpers/get-valid-attribution";

import Alert from "../../styles/components/Alert";
import Button from "../../styles/components/Button";
import EmailOptIn from "../email-opt-in";
import Form from "../form";
import Group from "../form/components/Group";
import Label from "../form/components/Label";
import Modal from "../modal";
import Recaptcha from "../recaptcha";
import { RecaptchaDisclaimer } from "../recaptcha/components";
import Row from "../form/components/Row";
import Select from "../form/components/Select";
import Text from "../form/components/Text";
import TextArea from "../form/components/TextArea";

class ModalFind extends Component {
  constructor(props) {
    super(props);

    this.getRecaptchaToken = this.getRecaptchaToken.bind(this);
    this.handleApiError = this.handleApiError.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.postData = this.postData.bind(this);

    this.form = createRef();
    this.header = createRef();

    this.state = {
      company: "",
      companyError: null,
      email: "",
      emailError: null,
      inProgress: false,
      message: "",
      messageError: null,
      name: "",
      nameError: null,
      phone: "",
      phoneError: null,
      serverError: null,
      success: false,
      time: "",
      timeError: null,
      zip: "",
      zipError: null,
    };
  }

  /**
   * Get reCaptcha token
   * @public
   */
  getRecaptchaToken() {
    const { handleApiError } = this;
    const { recaptchaLoaded, requestRecaptchaToken } = this.props;

    if (!recaptchaLoaded || !requestRecaptchaToken) {
      this.handleApiError("reCAPTCHA did not load");
      return false;
    }

    requestRecaptchaToken("contact")
      .then(token => {
        if (!token) {
          handleApiError("reCAPTCHA failed to execute");
          return false;
        }

        this.postData(token);
      })
      .catch(error => handleApiError(error));
  }

  /**
   * Handle API error
   * @param {*}
   * @public
   */
  handleApiError(error) {
    const message = getApiErrorMessage(error);

    this.setState({ inProgress: false, serverError: message }, () =>
      scrollIfNeeded(this.header.current)
    );
  }

  /**
   * Handle input change
   * @param {event|object} e
   * @public
   */
  handleInput(e) {
    const { target: input } = e;
    const field = input.id;
    const value = !!input ? input.value : null;
    const error = validateInput(input);

    if (!!field) {
      this.setState({ [field]: value, [`${field}Error`]: error });
    }
  }

  /**
   * Handle form submission
   * @param {event} e
   * @public
   */
  handleSubmit(e) {
    e.preventDefault();

    const { current: form } = this.form;

    if (!form) {
      return false;
    }

    if (!form.checkValidity()) {
      [...form.elements].forEach(input => {
        this.handleInput({ target: input });
      });
    } else {
      this.getRecaptchaToken();
    }
  }

  /**
   * Post data to API
   * @public
   */
  postData(recaptchaToken) {
    this.setState({ inProgress: true });

    const { handleApiError } = this;
    const { attribution, emailOptIn, searchParams } = this.props;
    const { company, email, message, name, phone, time, zip } = this.state;
    const { hasAttribution, validAttribution } =
      getValidAttribution(attribution);
    const data = {
      attribution: validAttribution,
      company: company,
      email: email,
      emailOptIn: emailOptIn,
      hasAttribution,
      message: !!message ? message.replace(/(?:\r\n|\r|\n)/g, "<br>") : null,
      name: name,
      phone: !!phone
        ? phone.replace(/.*(\d{3}).*(\d{3}).*(\d{4})(.*)/, "$1-$2-$3$4")
        : null,
      recaptchaToken: recaptchaToken,
      searchParams: searchParams,
      time: time,
      zip: zip,
    };

    if (!!searchParams) {
      const sourceDivision = searchParams.find(
        sp => sp.param === "sourceDivision"
      );

      if (!!sourceDivision && !!sourceDivision.value) {
        data.sourceDivision = unformatSourceDivision(
          sourceDivision.value,
          true
        );
      }
    }

    api
      .post(`${URL_API_LISTINGS_CONTACT}`, data)
      .then(response => {
        if (!!response.data && !!response.data.success) {
          if (!!this.props.trackEvent) {
            this.props.trackEvent({
              category: "Form",
              action: "Submit",
              label: "Find It For Me Modal",
            });
          }

          this.setState(
            { inProgress: false, serverError: null, success: true },
            () => scrollIfNeeded(this.header.current)
          );
        } else {
          handleApiError(response);
        }
      })
      .catch(handleApiError);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.props.open && prevProps.open) {
      this.setState({
        serverError: null,
        success: false,
      });
    }

    if (!prevProps.open && this.props.open && !!this.props.trackModalView) {
      this.props.trackModalView("find");
    }
  }

  render() {
    const { form, handleInput, handleSubmit, header } = this;
    const { close, open, targetDevice } = this.props;
    const {
      company,
      companyError,
      email,
      emailError,
      inProgress,
      message,
      messageError,
      name,
      nameError,
      phone,
      phoneError,
      serverError,
      success,
      time,
      timeError,
      zip,
      zipError,
    } = this.state;
    const shouldStack = targetDevice === "mobile";

    return (
      <Modal aria-label="Find This for Me" close={close} open={open}>
        <Recaptcha />
        <header ref={header}>
          <h1>
            <strong>Find This for Me</strong>
          </h1>
          {serverError && <Alert type="error">{serverError}</Alert>}
          {success && (
            <Alert type="success">Thanks! We’ll be in touch soon.</Alert>
          )}
        </header>
        {!success && (
          <Form onSubmit={handleSubmit} formRef={form}>
            <div className="form-fields">
              <Row stack={shouldStack}>
                <Group error={nameError}>
                  <Label htmlFor="name" required>
                    Name
                  </Label>
                  <Text
                    id="name"
                    maxLength="30"
                    onChange={handleInput}
                    required
                    value={name}
                  />
                </Group>
                <Group error={phoneError}>
                  <Label htmlFor="phone" required>
                    Phone Number
                  </Label>
                  <Text
                    id="phone"
                    maxLength="20"
                    onChange={handleInput}
                    required
                    type="tel"
                    value={phone}
                  />
                </Group>
              </Row>
              <Group error={emailError}>
                <Label htmlFor="email" required>
                  Email
                </Label>
                <Text
                  id="email"
                  maxLength="200"
                  onChange={handleInput}
                  required
                  type="email"
                  value={email}
                />
              </Group>
              <Row stack={shouldStack}>
                <Group error={companyError}>
                  <Label htmlFor="company">Company</Label>
                  <Text
                    id="company"
                    maxLength="200"
                    onChange={handleInput}
                    value={company}
                  />
                </Group>
                <Group error={zipError}>
                  <Label htmlFor="zip" required>
                    ZIP Code
                  </Label>
                  <Text
                    id="zip"
                    maxLength="10"
                    onChange={handleInput}
                    required
                    value={zip}
                  />
                </Group>
              </Row>
              <Group error={messageError}>
                <Label htmlFor="message">Message</Label>
                <TextArea id="message" onChange={handleInput} value={message} />
              </Group>
              <Row>
                <Group
                  className={targetDevice === "desktop" ? "w50" : null}
                  error={timeError}
                  labelOffset={targetDevice === "desktop" ? "medium" : null}
                >
                  <Label htmlFor="time">Best time to reach me</Label>
                  <Select
                    defaultOption="Select Time of Day"
                    id="time"
                    onChange={handleInput}
                    options={[
                      { value: "Morning" },
                      { value: "Afternoon" },
                      { value: "Evening" },
                    ]}
                    value={time}
                  />
                </Group>
              </Row>
              <EmailOptIn labelOffset="medium" />
            </div>
            <RecaptchaDisclaimer className="form-disclaimer" />
            <div className="form-actions">
              <Button
                block={targetDevice === "mobile"}
                color="green400"
                disabled={inProgress}
                size="small"
                square={targetDevice === "mobile"}
                type="submit"
              >
                {inProgress ? "Sending…" : "Send"}
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    );
  }
}

ModalFind.propTypes = {
  close: func.isRequired,
  emailOptIn: bool,
  open: bool,
  phoneInit: string,
  recaptchaLoaded: bool,
  requestRecaptchaToken: func.isRequired,
  searchParams: array,
  targetDevice: string,
  user: shape({
    email: string,
    name: string,
  }),
};

ModalFind.defaultProps = {
  open: false,
};

ModalFind.getDerivedStateFromProps = (nextProps, prevState) => {
  const { phoneInit, user } = nextProps;
  const { email, name, phone } = prevState;
  const nextState = {};

  if (!!user && !!user.email && !email) {
    nextState.email = user.email;
  }

  if (!!user && !!user.name && !name) {
    nextState.name = user.name;
  }

  if (!!phoneInit) {
    nextState.phone = phoneInit;
  } else if (!!user && !!user.phone && !phone) {
    nextState.phone = user.phone;
  }

  return !!Object.keys(nextState).length ? nextState : null;
};

export default ModalFind;
